import { Col, Input, Row, Text } from 'components';
import { theme } from 'lib';
import React from 'react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import styled from 'styled-components';
import {
  BONUS_ELIGIBILITY,
  CAP_BEHAVIOR,
  fmtCount,
  HIGH_TAP_TARGET,
  REWARD_LIMITS,
  tapQuotaCeiling,
  tapQuotaEnabled,
  tapQuotaTargetError,
  uncappedPayoutWarning
} from '../constants';
import { blockNonInteger, NumberField, RadioOption, toDigits, ToggleHeader } from './ui';

/**
 * FanWave reward rules editor — mockup sections 3–8 of "Proposed mockup contents of Fan Wave
 * on CMS". One controlled component over the full rewards object; used by the create form
 * (per-wave overrides) and the Global Defaults screen (platform rules).
 *
 * Section 5 (Community Goal) also carries the tap target, which is NOT a rewards field —
 * the parent owns it (wave `target` / `globalTarget`) and passes it through.
 */

/**
 * What the tap-quota field starts at when an admin switches the limit ON from an uncapped
 * wave. Only a starting point for the input — the stored "off" value is 0.
 */
const DEFAULT_TAP_QUOTA = 100;

// Sub-card for one mockup section, nested inside the parent Section card.
const SectionBox = ({ children }) => (
  <Col
    noFlex
    wid="100%"
    pad="16px 18px"
    gap="14px"
    style={{
      background: theme.GREY_10,
      borderRadius: '12px',
      border: `1px solid ${theme.GREY_20}`
    }}
  >
    {children}
  </Col>
);

const SectionTitle = ({ number, title, description }) => (
  <Col noFlex wid="100%">
    <Text b2 bold marg="0" style={{ fontSize: '13.5px' }}>
      {number != null ? `${number}. ` : ''}
      {title}
    </Text>
    {description && (
      <Text c1 col={theme.GREY_60} marg="2px 0 0" style={{ fontSize: '11.5px' }}>
        {description}
      </Text>
    )}
  </Col>
);

const AddRowButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 14px;
  border: 1px solid ${theme.SECONDARY_20};
  border-radius: 8px;
  background: #fff;
  color: ${theme.SECONDARY};
  font-family: CeraPro;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  width: fit-content;

  &:hover:not(:disabled) {
    background: ${theme.SECONDARY_10};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const DeleteRowButton = styled.button`
  background: none;
  border: none;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 6px;
  padding: 0;
  color: ${theme.GREY_50};
  flex-shrink: 0;

  &:hover {
    color: #dc2626;
    background: #fdecec;
  }
`;

// Compact digit-only cell used inside the milestone / rank tables.
const TableNumberInput = ({ value, onChange, placeholder }) => (
  <Input
    type="number"
    inputMode="numeric"
    value={value}
    onChange={(e) => onChange(toDigits(e.target.value))}
    onKeyDown={blockNonInteger}
    placeholder={placeholder}
  />
);

const ColumnLabel = ({ children }) => (
  <Text
    c1
    col={theme.GREY_50}
    marg="0"
    style={{
      flex: 1,
      fontSize: '10.5px',
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase'
    }}
  >
    {children}
  </Text>
);

// Reserves the same space the row's DeleteRowButton occupies (30px width + the
// Row's 10px gap) so header labels line up with the inputs above them.
const DeleteColumnSpacer = () => <div style={{ width: '30px', flexShrink: 0 }} />;

const InlineError = ({ children }) => (
  <Text c1 col={theme.SECONDARY} marg="0" style={{ fontSize: '11.5px' }}>
    {children}
  </Text>
);

// "10 taps = 20 pts …" preview mirroring the mockup's Participation Rewards panel.
const cyclePreviewLines = (rewards) => {
  const taps = Number(rewards.tapsPerCycle) || 0;
  const points = Number(rewards.basePoints) || 0;
  if (taps <= 0) return [];
  if (!rewards.allowMultipleCycles) return [`${taps} taps = ${points} pts (earned once)`];
  return [1, 2, 3].map((cycle) => `${taps * cycle} taps = ${points} pts`).concat(['…']);
};

const RewardsFields = ({
  rewards,
  onChange,
  // Section 5's tap target is owned by the parent (per-wave target / globalTarget).
  target,
  // The target the wave will ACTUALLY run on — differs from `target` when the field is left
  // empty to inherit the channel's. Bounds the per-fan tap quota; defaults to `target` so a
  // caller with nothing to inherit need not pass both.
  effectiveTarget = target,
  onTargetChange,
  targetError,
  targetDescription,
  // Show the mockup's 3–8 numbering (create form) or plain titles (global defaults).
  numbered = true,
  // field key → server-side error message, rendered inside the owning section.
  errors = {}
}) => {
  const patch = (fields) => onChange({ ...rewards, ...fields });

  const num = (n) => (numbered ? n : null);

  // The per-fan tap quota is bounded by the COLLECTIVE target as well as by its own hard
  // maximum, so the field advertises whichever binds first and errors inline the moment the
  // typed value passes it — `validateRewards` blocks the save on the same rule.
  const quotaCeiling = tapQuotaCeiling(effectiveTarget);
  const quotaError = tapQuotaTargetError(rewards, effectiveTarget);

  const updateMilestone = (i, field, value) => {
    const next = rewards.milestones.map((m, idx) => (idx === i ? { ...m, [field]: value } : m));
    patch({ milestones: next });
  };
  const updateRankReward = (i, field, value) => {
    const next = rewards.rankRewards.map((r, idx) => (idx === i ? { ...r, [field]: value } : r));
    patch({ rankRewards: next });
  };

  return (
    <Col noFlex wid="100%" gap="14px">
      {/* ——— Section 3: Participation Rewards ——— */}
      <SectionBox>
        <SectionTitle
          number={num(3)}
          title="Participation Rewards"
          description="Configure how fans earn individual points."
        />
        <Row start gap="18px" style={{ alignItems: 'flex-start' }}>
          <NumberField
            label="Taps Required To Earn Points"
            description="Fans must tap this many times to earn a reward."
            value={rewards.tapsPerCycle}
            onChange={(v) => patch({ tapsPerCycle: v })}
            error={errors.tapsPerCycle}
          />
          <NumberField
            label="Base Reward Points"
            description="Points awarded per completion."
            value={rewards.basePoints}
            onChange={(v) => patch({ basePoints: v })}
            error={errors.basePoints}
          />
        </Row>
        <Row start between gap="18px" wid="100%" style={{ alignItems: 'stretch' }}>
          <Col noFlex style={{ flex: '1 1 220px', justifyContent: 'center' }}>
            <ToggleHeader
              title="Allow Multiple Reward Cycles"
              description="Fans can continue earning rewards after each cycle."
              on={rewards.allowMultipleCycles}
              onToggle={() => patch({ allowMultipleCycles: !rewards.allowMultipleCycles })}
            />
          </Col>
          <Col
            noFlex
            pad="10px 14px"
            gap="2px"
            style={{
              flex: '1 1 180px',
              background: theme.SECONDARY_10,
              borderRadius: '10px',
              border: `1px solid ${theme.SECONDARY_20}`
            }}
          >
            <Text
              c1
              col={theme.GREY_60}
              marg="0 0 2px"
              style={{
                fontSize: '10.5px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em'
              }}
            >
              Preview
            </Text>
            {cyclePreviewLines(rewards).map((line, i) => (
              <Text key={i} c1 col={theme.GREY_70} marg="0" style={{ fontSize: '12px' }}>
                {line}
              </Text>
            ))}
          </Col>
        </Row>
      </SectionBox>

      {/* ——— Section 4: Progressive Rewards ——— */}
      <SectionBox>
        <ToggleHeader
          title={`${numbered ? '4. ' : ''}Progressive Rewards`}
          description="Reward fans more as they keep tapping. Replaces the flat cycle rewards when enabled."
          on={rewards.progressiveEnabled}
          onToggle={() => patch({ progressiveEnabled: !rewards.progressiveEnabled })}
        />
        {errors.milestones && <InlineError>{errors.milestones}</InlineError>}
        {rewards.progressiveEnabled && (
          <>
            {rewards.milestones.length > 0 && (
              <Col noFlex wid="100%" gap="8px" style={{ maxWidth: '520px' }}>
                <Row start gap="10px" wid="100%">
                  <ColumnLabel>Total Taps</ColumnLabel>
                  <ColumnLabel>Reward Points</ColumnLabel>
                  <DeleteColumnSpacer />
                </Row>
                {rewards.milestones.map((m, i) => (
                  <Row key={i} center gap="10px" wid="100%">
                    <Col noFlex style={{ flex: 1, minWidth: 0 }}>
                      <TableNumberInput
                        value={m.taps}
                        onChange={(v) => updateMilestone(i, 'taps', v)}
                        placeholder="Taps"
                      />
                    </Col>
                    <Col noFlex style={{ flex: 1, minWidth: 0 }}>
                      <TableNumberInput
                        value={m.points}
                        onChange={(v) => updateMilestone(i, 'points', v)}
                        placeholder="Points"
                      />
                    </Col>
                    <DeleteRowButton
                      type="button"
                      title="Remove milestone"
                      onClick={() =>
                        patch({ milestones: rewards.milestones.filter((_, idx) => idx !== i) })
                      }
                    >
                      <FiTrash2 size={15} />
                    </DeleteRowButton>
                  </Row>
                ))}
              </Col>
            )}
            <AddRowButton
              type="button"
              disabled={rewards.milestones.length >= REWARD_LIMITS.MAX_MILESTONES}
              onClick={() =>
                patch({ milestones: [...rewards.milestones, { taps: '', points: '' }] })
              }
            >
              <FiPlus size={14} />
              Add Milestone
            </AddRowButton>
            <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: '11px' }}>
              Cumulative total taps reached → points awarded for that tier (e.g. 10 → 10 pts, 20 →
              15 pts). Max {REWARD_LIMITS.MAX_MILESTONES} milestones.
            </Text>
          </>
        )}
      </SectionBox>

      {/* ——— Section 5: Community Goal ——— */}
      <SectionBox>
        <SectionTitle
          number={num(5)}
          title="Community Goal"
          description="Set the collective target and messaging."
        />
        <Row start gap="18px" style={{ alignItems: 'flex-start' }}>
          <NumberField
            label="Target Taps"
            description={targetDescription || 'Total taps needed from the community.'}
            value={target}
            onChange={onTargetChange}
            error={targetError}
          />
          <Col noFlex style={{ flex: '1 1 180px', minWidth: '160px', maxWidth: '260px' }}>
            <Text b2 bold marg="0 0 5px" style={{ fontSize: '13px' }}>
              Headline
            </Text>
            <Input
              value={rewards.headline}
              onChange={(e) =>
                patch({ headline: e.target.value.slice(0, REWARD_LIMITS.MAX_HEADLINE_LEN) })
              }
              placeholder="Ride The Wave"
            />
            <Text
              c1
              col={errors.headline ? theme.SECONDARY : theme.GREY_60}
              marg="5px 0 0"
              style={{ fontSize: '11px' }}
            >
              {errors.headline || 'Appears at top of the FanWave card.'}
            </Text>
          </Col>
          <Col noFlex style={{ flex: '1 1 180px', minWidth: '160px', maxWidth: '260px' }}>
            <Text b2 bold marg="0 0 5px" style={{ fontSize: '13px' }}>
              Goal Message
            </Text>
            <Input
              value={rewards.goalMessage}
              onChange={(e) =>
                patch({ goalMessage: e.target.value.slice(0, REWARD_LIMITS.MAX_GOAL_MESSAGE_LEN) })
              }
              placeholder="Hit 2000!"
            />
            <Text
              c1
              col={errors.goalMessage ? theme.SECONDARY : theme.GREY_60}
              marg="5px 0 0"
              style={{ fontSize: '11px' }}
            >
              {errors.goalMessage || 'Appears next to the headline.'}
            </Text>
          </Col>
        </Row>
      </SectionBox>

      {/* ——— Section 6: Community Bonus ——— */}
      <SectionBox>
        <ToggleHeader
          title={`${numbered ? '6. ' : ''}Community Bonus`}
          description="Award bonus points when the community hits the target."
          on={rewards.communityBonusEnabled}
          onToggle={() => patch({ communityBonusEnabled: !rewards.communityBonusEnabled })}
        />
        {rewards.communityBonusEnabled && (
          <Row start between gap="18px" wid="100%" style={{ alignItems: 'flex-start' }}>
            <NumberField
              label="Bonus Points"
              description="Points awarded to eligible participants."
              value={rewards.communityBonusPoints}
              onChange={(v) => patch({ communityBonusPoints: v })}
              error={errors.communityBonusPoints}
            />
            <Col noFlex gap="10px" style={{ flex: '1 1 260px' }}>
              <Text b2 bold marg="0" style={{ fontSize: '13px' }}>
                Eligible Participants
              </Text>
              <RadioOption
                checked={rewards.bonusEligibility === BONUS_ELIGIBILITY.ALL}
                onSelect={() => patch({ bonusEligibility: BONUS_ELIGIBILITY.ALL })}
                label="All Participants"
                description="Every fan who tapped at least once."
              />
              <RadioOption
                checked={rewards.bonusEligibility === BONUS_ELIGIBILITY.MIN_TAPS}
                onSelect={() => patch({ bonusEligibility: BONUS_ELIGIBILITY.MIN_TAPS })}
                label="Only users who completed at least one cycle"
                description="Fans must reach the taps-per-cycle threshold to qualify."
              />
            </Col>
          </Row>
        )}
      </SectionBox>

      {/* ——— Section 7a: Reward Cap ———
          Numbered 7a, not 7, because it is the first of a PAIR with 7b below: both cap one
          fan, and reading "7" next to "7b" left 7a looking absent. ——— */}
      <SectionBox>
        <ToggleHeader
          title={`${numbered ? '7a. ' : ''}Reward Cap`}
          description="Limit the maximum points a fan can earn."
          on={rewards.capEnabled}
          onToggle={() => patch({ capEnabled: !rewards.capEnabled })}
        />
        {rewards.capEnabled && (
          <Row start between gap="18px" wid="100%" style={{ alignItems: 'flex-start' }}>
            <NumberField
              label="Maximum Earnable Points"
              description="Fans stop earning points after this limit."
              value={rewards.maxPoints}
              onChange={(v) => patch({ maxPoints: v })}
              error={errors.maxPoints}
            />
            <Col noFlex gap="10px" style={{ flex: '1 1 260px' }}>
              <Text b2 bold marg="0" style={{ fontSize: '13px' }}>
                After Limit Reached
              </Text>
              <RadioOption
                checked={rewards.capBehavior === CAP_BEHAVIOR.CONTINUE}
                onSelect={() => patch({ capBehavior: CAP_BEHAVIOR.CONTINUE })}
                label="Continue tapping but no more points"
                description="Taps still count toward the community target."
              />
              <RadioOption
                checked={rewards.capBehavior === CAP_BEHAVIOR.DISABLE}
                onSelect={() => patch({ capBehavior: CAP_BEHAVIOR.DISABLE })}
                label="Disable participation"
                description="The fan can no longer tap once capped."
              />
            </Col>
          </Row>
        )}
      </SectionBox>

      {/* ——— Section 7b: Tap Limit Per Fan ———
          Deliberately its own card, directly under the Reward Cap it is confused with: that one
          bounds the POINTS a fan earns, this one bounds the TAPS they may take at all. */}
      <SectionBox>
        <ToggleHeader
          title={`${numbered ? '7b. ' : ''}Tap Limit Per Fan`}
          description="Give each fan a finite number of taps for this wave. Different from the Reward Cap above — that limits points earned, this limits taps taken."
          on={rewards.tapQuotaEnabled}
          onToggle={() =>
            patch({
              tapQuotaEnabled: !rewards.tapQuotaEnabled,
              // Turning it on with the stored 0 would read as "0 taps allowed" — seed a value
              // the admin can then change; turning it off keeps what they typed for next time.
              maxTapsPerUser:
                !rewards.tapQuotaEnabled && !Number(rewards.maxTapsPerUser)
                  ? String(DEFAULT_TAP_QUOTA)
                  : rewards.maxTapsPerUser
            })
          }
        />
        {rewards.tapQuotaEnabled ? (
          <Row start between gap="18px" wid="100%" style={{ alignItems: 'flex-start' }}>
            <NumberField
              label="Maximum Taps Per Fan"
              description={`1–${fmtCount(
                quotaCeiling
              )}. Taps beyond this count for nothing — not for the fan, not for the community total.`}
              value={rewards.maxTapsPerUser}
              onChange={(v) => patch({ maxTapsPerUser: v })}
              error={quotaError || errors.maxTapsPerUser}
            />
            <Col noFlex gap="4px" style={{ flex: '1 1 260px' }}>
              <Text b2 bold marg="0" style={{ fontSize: '13px' }}>
                Not a rate limit
              </Text>
              <Text
                c1
                col={theme.GREY_60}
                marg="0"
                style={{ fontSize: '11.5px', lineHeight: 1.45 }}
              >
                Fast tapping is never refused. A fan simply has this many taps for the wave, and
                keeps every one of them.
              </Text>
            </Col>
          </Row>
        ) : (
          <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: '11.5px' }}>
            No tap limit — fans can tap as many times as they like.
          </Text>
        )}
      </SectionBox>

      {/* ——— Section 8: Advanced Rewards ——— */}
      <SectionBox>
        <ToggleHeader
          title={`${numbered ? '8. ' : ''}Advanced Rewards (Optional)`}
          description="Reward your top contributors by leaderboard rank at wave close."
          on={rewards.rankRewardsEnabled}
          onToggle={() => patch({ rankRewardsEnabled: !rewards.rankRewardsEnabled })}
        />
        {errors.rankRewards && <InlineError>{errors.rankRewards}</InlineError>}
        {rewards.rankRewardsEnabled && (
          <>
            {rewards.rankRewards.length > 0 && (
              <Col noFlex wid="100%" gap="8px" style={{ maxWidth: '640px' }}>
                <Row start gap="10px" wid="100%">
                  <ColumnLabel>From Rank</ColumnLabel>
                  <ColumnLabel>To Rank</ColumnLabel>
                  <ColumnLabel>Bonus Points</ColumnLabel>
                  <DeleteColumnSpacer />
                </Row>
                {rewards.rankRewards.map((r, i) => (
                  <Row key={i} center gap="10px" wid="100%">
                    <Col noFlex style={{ flex: 1, minWidth: 0 }}>
                      <TableNumberInput
                        value={r.fromRank}
                        onChange={(v) => updateRankReward(i, 'fromRank', v)}
                        placeholder="#1"
                      />
                    </Col>
                    <Col noFlex style={{ flex: 1, minWidth: 0 }}>
                      <TableNumberInput
                        value={r.toRank}
                        onChange={(v) => updateRankReward(i, 'toRank', v)}
                        placeholder="#5"
                      />
                    </Col>
                    <Col noFlex style={{ flex: 1, minWidth: 0 }}>
                      <TableNumberInput
                        value={r.points}
                        onChange={(v) => updateRankReward(i, 'points', v)}
                        placeholder="Points"
                      />
                    </Col>
                    <DeleteRowButton
                      type="button"
                      title="Remove rank band"
                      onClick={() =>
                        patch({ rankRewards: rewards.rankRewards.filter((_, idx) => idx !== i) })
                      }
                    >
                      <FiTrash2 size={15} />
                    </DeleteRowButton>
                  </Row>
                ))}
              </Col>
            )}
            <AddRowButton
              type="button"
              disabled={rewards.rankRewards.length >= REWARD_LIMITS.MAX_RANK_REWARDS}
              onClick={() =>
                patch({
                  rankRewards: [...rewards.rankRewards, { fromRank: '', toRank: '', points: '' }]
                })
              }
            >
              <FiPlus size={14} />
              Add Rank
            </AddRowButton>
            <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: '11px' }}>
              Rank bands, e.g. #1 → 100 pts, #2–#5 → 50 pts, #6–#20 → 25 pts. Max{' '}
              {REWARD_LIMITS.MAX_RANK_REWARDS} bands.
            </Text>
          </>
        )}
      </SectionBox>
    </Col>
  );
};

/**
 * Client-side validation mirroring `fanwaveRewardsJoiSchema`. Returns the first problem as
 * an error message string, or null when the rules are valid. Number fields arrive as
 * strings from the inputs — parents normalize via `normalizeRewards` before submitting.
 *
 * `target` is the EFFECTIVE collective target the wave will run on (an empty per-wave target
 * inherits the channel's, so pass that one) — it bounds the per-fan tap quota. Omit it and only
 * that one cross-field rule is skipped; it is the sole check that looks outside `rewards`.
 */
export const validateRewards = (rewards, target) => {
  const taps = Number(rewards.tapsPerCycle);
  if (!taps || taps < REWARD_LIMITS.MIN_TAPS_PER_CYCLE || taps > REWARD_LIMITS.MAX_TAPS_PER_CYCLE) {
    return `Taps per cycle must be between ${REWARD_LIMITS.MIN_TAPS_PER_CYCLE} and ${REWARD_LIMITS.MAX_TAPS_PER_CYCLE}`;
  }
  const base = Number(rewards.basePoints);
  if (!Number.isFinite(base) || rewards.basePoints === '' || base > REWARD_LIMITS.MAX_BASE_POINTS) {
    return `Base points must be between 0 and ${REWARD_LIMITS.MAX_BASE_POINTS}`;
  }
  if (rewards.progressiveEnabled) {
    if (!rewards.milestones.length) return 'Add at least one progressive milestone';
    for (const m of rewards.milestones) {
      if (!Number(m.taps) || Number(m.taps) < 1)
        return 'Every milestone needs a taps value of 1 or more';
      if (m.points === '' || Number(m.points) < 0) return 'Every milestone needs a points value';
    }
  }
  if (rewards.communityBonusEnabled) {
    const bonus = Number(rewards.communityBonusPoints);
    if (
      !Number.isFinite(bonus) ||
      rewards.communityBonusPoints === '' ||
      bonus > REWARD_LIMITS.MAX_COMMUNITY_BONUS_POINTS
    ) {
      return `Community bonus points must be between 0 and ${REWARD_LIMITS.MAX_COMMUNITY_BONUS_POINTS}`;
    }
  }
  if (rewards.capEnabled) {
    const cap = Number(rewards.maxPoints);
    if (!cap || cap < 1 || cap > REWARD_LIMITS.MAX_CAP_POINTS) {
      return `Maximum earnable points must be between 1 and ${REWARD_LIMITS.MAX_CAP_POINTS}`;
    }
  }
  if (rewards.tapQuotaEnabled) {
    const quota = Number(rewards.maxTapsPerUser);
    if (!Number.isInteger(quota) || quota < 1 || quota > REWARD_LIMITS.MAX_TAPS_PER_USER) {
      return `Maximum taps per fan must be between 1 and ${fmtCount(
        REWARD_LIMITS.MAX_TAPS_PER_USER
      )} (switch the limit off for no cap)`;
    }
    const quotaVsTarget = tapQuotaTargetError(rewards, target);
    if (quotaVsTarget) return quotaVsTarget;
    // The API accepts this, but the wave then pays out NOTHING: no fan can ever reach the end
    // of a cycle, so no points are ever earned. Blocked here rather than warned about.
    if (quota < Number(rewards.tapsPerCycle)) {
      return `A ${quota}-tap limit is below the ${rewards.tapsPerCycle} taps needed to earn — no fan could complete a cycle, so the wave would pay out nothing`;
    }
  }
  if (rewards.rankRewardsEnabled) {
    if (!rewards.rankRewards.length) return 'Add at least one rank band';
    for (const r of rewards.rankRewards) {
      const from = Number(r.fromRank);
      const to = Number(r.toRank);
      if (!from || from < 1) return 'Every rank band needs a from-rank of 1 or more';
      if (!to || to < from) return 'Every rank band needs a to-rank ≥ its from-rank';
      if (r.points === '' || Number(r.points) < 0) return 'Every rank band needs a points value';
    }
  }
  return null;
};

/**
 * Configurations the API accepts but that don't do what an admin expects — surfaced next to the
 * save button rather than blocking it (`validateRewards` blocks the one that pays out nothing).
 * Returns an array of messages, empty when there is nothing to flag.
 */
export const rewardsWarnings = (rewards, target) => {
  const warnings = [];
  if (!rewards) return warnings;
  const quota = rewards.tapQuotaEnabled ? Number(rewards.maxTapsPerUser) : 0;

  // Repeat cycles with NO tap quota: the claim falls back to the COLLECTIVE target as one fan's
  // tap ceiling, so the most a single fan can take home scales with the target. The API accepts
  // the combination and does not enforce anything here, which is exactly why it is said out loud.
  if (!quota && rewards.allowMultipleCycles && Number(target) > HIGH_TAP_TARGET) {
    warnings.push(uncappedPayoutWarning(rewards, target));
  }

  if (!quota) return warnings;

  // A milestone past the quota is a tier no fan can ever reach — the app stops offering it as
  // the "next reward" precisely because it is unreachable.
  if (rewards.progressiveEnabled) {
    const unreachable = (rewards.milestones || []).filter((m) => Number(m.taps) > quota);
    if (unreachable.length) {
      warnings.push(
        `${unreachable.length === 1 ? 'A progressive milestone sits' : `${unreachable.length} progressive milestones sit`} above the ${quota}-tap limit (${unreachable
          .map((m) => `${m.taps} taps`)
          .join(', ')}) — no fan can reach ${unreachable.length === 1 ? 'it' : 'them'}.`
      );
    }
  }
  // Repeat cycles stop at the quota, and a quota that isn't a whole number of cycles leaves
  // every fan with taps they can never turn into points.
  if (!rewards.progressiveEnabled && rewards.allowMultipleCycles) {
    const perCycle = Number(rewards.tapsPerCycle);
    const leftover = perCycle > 0 ? quota % perCycle : 0;
    if (leftover) {
      const cycles = Math.floor(quota / perCycle);
      warnings.push(
        `${quota} taps is not a whole number of ${perCycle}-tap cycles — a fan can complete ${cycles}, and their last ${leftover} tap${leftover === 1 ? '' : 's'} earn nothing.`
      );
    }
  }
  return warnings;
};

/** Input state (digit strings) → API payload (numbers), preserving booleans/enums/copy. */
export const normalizeRewards = (rewards) => ({
  tapsPerCycle: Number(rewards.tapsPerCycle),
  basePoints: Number(rewards.basePoints),
  allowMultipleCycles: !!rewards.allowMultipleCycles,
  progressiveEnabled: !!rewards.progressiveEnabled,
  milestones: rewards.milestones.map((m) => ({ taps: Number(m.taps), points: Number(m.points) })),
  headline: rewards.headline?.trim() || undefined,
  goalMessage: rewards.goalMessage?.trim() || undefined,
  communityBonusEnabled: !!rewards.communityBonusEnabled,
  communityBonusPoints: Number(rewards.communityBonusPoints) || 0,
  bonusEligibility: rewards.bonusEligibility,
  capEnabled: !!rewards.capEnabled,
  maxPoints: Number(rewards.maxPoints) || 0,
  capBehavior: rewards.capBehavior,
  // `tapQuotaEnabled` is the form's own switch — the API stores the limit in one field, where
  // 0 IS "uncapped", so switching it off sends 0 rather than dropping the field.
  maxTapsPerUser: rewards.tapQuotaEnabled ? Number(rewards.maxTapsPerUser) || 0 : 0,
  rankRewardsEnabled: !!rewards.rankRewardsEnabled,
  rankRewards: rewards.rankRewards.map((r) => ({
    fromRank: Number(r.fromRank),
    toRank: Number(r.toRank),
    points: Number(r.points)
  }))
});

/** API rewards (numbers, possibly missing copy fields) → editable input state. */
export const toEditableRewards = (rewards) => ({
  tapsPerCycle: String(rewards?.tapsPerCycle ?? 10),
  basePoints: String(rewards?.basePoints ?? 10),
  allowMultipleCycles: !!rewards?.allowMultipleCycles,
  progressiveEnabled: !!rewards?.progressiveEnabled,
  milestones: (rewards?.milestones || []).map((m) => ({
    taps: String(m.taps),
    points: String(m.points)
  })),
  headline: rewards?.headline || '',
  goalMessage: rewards?.goalMessage || '',
  communityBonusEnabled: !!rewards?.communityBonusEnabled,
  communityBonusPoints: String(rewards?.communityBonusPoints ?? 0),
  bonusEligibility: rewards?.bonusEligibility || BONUS_ELIGIBILITY.ALL,
  capEnabled: !!rewards?.capEnabled,
  maxPoints: String(rewards?.maxPoints ?? 0),
  capBehavior: rewards?.capBehavior || CAP_BEHAVIOR.CONTINUE,
  // 0 is the API's "uncapped", which reads as an off switch here — never as "0 taps allowed".
  tapQuotaEnabled: tapQuotaEnabled(rewards?.maxTapsPerUser),
  maxTapsPerUser: String(rewards?.maxTapsPerUser ?? 0),
  rankRewardsEnabled: !!rewards?.rankRewardsEnabled,
  rankRewards: (rewards?.rankRewards || []).map((r) => ({
    fromRank: String(r.fromRank),
    toRank: String(r.toRank),
    points: String(r.points)
  }))
});

export default RewardsFields;
